import {InjectionToken} from '@angular/core';
import {DateTime} from 'luxon';
import {
    AllocationUnitStatus,
    ConsumptionChartData,
    ConsumptionData,
    Interval,
} from '@brunata-angular/consumption-chart';

export type ConsumptionOverviewState = {
    readonly allocationUnit: string | null;
    readonly interval: Interval;
    readonly periodOffset: number;
    readonly compare: boolean;
};

// Provided by the fullsize popup so the chart opens on the view the administrator was looking at.
export const CONSUMPTION_OVERVIEW_INITIAL_STATE = new InjectionToken<ConsumptionOverviewState>(
    'CONSUMPTION_OVERVIEW_INITIAL_STATE',
);

/**
 * The last date this consumer can have consumption for on the selected allocation unit: the unit's
 * last dismount when no meter is left, the move-out date when the consumer has left, whichever
 * came first. Null means the data runs up to today.
 */
export function lastDateWithConsumption(
    unitStatus: AllocationUnitStatus | null,
    occupationEndDate: DateTime | null,
    now: DateTime,
): DateTime | null {
    const candidates: DateTime[] = [];
    if (unitStatus != null && !unitStatus.isActive && unitStatus.lastDismountedDate != null) {
        candidates.push(unitStatus.lastDismountedDate);
    }
    if (occupationEndDate != null && occupationEndDate < now) {
        candidates.push(occupationEndDate);
    }
    return DateTime.min(...candidates) ?? null;
}

export type ConsumptionTableRow = {
    readonly meterId: number;
    readonly color: string;
    /** The legend's name for the meter: placement, or the meter number when there is none. */
    readonly name: string;
    readonly meterNo: string;
    readonly isDismounted: boolean;
    readonly values: ReadonlyArray<number | null>;
    readonly total: number | null;
    readonly unit: string;
    readonly decimals: number;
};

export type ConsumptionTable = {
    readonly columns: ReadonlyArray<DateTime>;
    readonly rows: ReadonlyArray<ConsumptionTableRow>;
};

/**
 * One row per meter in meter-sequence order, one column per period bucket. Buckets are keyed on
 * their start so a meter mounted mid-period lines up with the others and shows dashes before that.
 */
export function buildConsumptionTable(
    consumption: ConsumptionData,
    chartData: ReadonlyArray<ConsumptionChartData>,
): ConsumptionTable {
    const columnByStart = new Map<number, DateTime>();
    for (const line of consumption.consumptionLines) {
        for (const value of line.consumptionValues) {
            const start = value.fromDate.toMillis();
            if (!columnByStart.has(start)) {
                columnByStart.set(start, value.fromDate);
            }
        }
    }
    const columns = [...columnByStart.entries()]
        .sort(([left], [right]) => left - right)
        .map(([, column]) => column);
    const chartDataByMeterId = new Map(chartData.map(item => [item.meterId, item]));

    const rows = [...consumption.consumptionLines]
        .sort((left, right) => left.meter.meterSequenceNo - right.meter.meterSequenceNo)
        .map((line): ConsumptionTableRow => {
            const valueByStart = new Map(
                line.consumptionValues.map(value => [value.fromDate.toMillis(), value.consumption]),
            );
            const values = columns.map(column => valueByStart.get(column.toMillis()) ?? null);
            const readings = values.filter(value => value !== null);
            const chartItem = chartDataByMeterId.get(line.meter.meterId);
            return {
                meterId: line.meter.meterId,
                color: chartItem?.color ?? 'transparent',
                name: chartItem?.meterName ?? line.meter.placement ?? line.meter.meterNo,
                meterNo: line.meter.meterNo,
                isDismounted: line.meter.dismountedDate !== null,
                values,
                total:
                    readings.length === 0
                        ? null
                        : readings.reduce((sum, reading) => sum + reading, 0),
                unit: line.meter.unit,
                decimals: line.meter.decimals,
            };
        });

    return {columns, rows};
}
